import React from 'react';
import {
  AuthType,
  IBasicConnectionInput,
  IBigQueryConnectionInput,
  IConnectionInput,
  StrategyKind,
} from '../../../../utils/apollo/graphqlSchema';
import DataModelerBasicConnectionInput from './DataModelerBasicConnectionInput';
import DataModelerBigQueryConnectionInput from './DataModelerBigQueryConnectionInput';
import { DataModelerConfigLabelWithInfo } from '../DataModelerShared';
import { dataModelLabelInfoTooltipOptions } from '../../../../components/KubitTooltip/globalTooltips';
import { InputField, VerticalField } from '../../../../components/Form';
import { useDataModelerEditSeFeature } from '../../../../utils/hooks';
import { canEditConnectionDetails } from './utils';

interface IProps {
  isNewSchema: boolean;
  readOnly: boolean;
  connection: IConnectionInput;
  onChange: (connection: IConnectionInput) => void;
  dateDt: string;
  setDateDt: (dateDt: string) => void;
}

const DataModelerConnectionInput = ({
  isNewSchema,
  readOnly,
  connection,
  onChange,
  dateDt,
  setDateDt,
}: IProps) => {
  const setBasicInput = (basicInput: IBasicConnectionInput) => {
    // XXX special-case - switch to bigQuery edit mode
    if (basicInput.strategy === StrategyKind.Bigquery) {
      onChange({
        basic: null,
        bigQuery: {
          account: '',
          dataset: '',
          keyPlain: '',
          projectId: '',
          computeProjectId: null,
          timeout: 600,
        },
      });
      return;
    }
    onChange({
      basic: {
        ...basicInput,
      },
      bigQuery: null,
    });
  };

  const switchFromBigQuery = (kind: StrategyKind) => {
    if (kind === StrategyKind.Bigquery) {
      return;
    }
    onChange({
      bigQuery: null,
      basic: {
        strategy: kind,
        jdbcUrl: '',
        username: '',
        password: '',
        privateKey: null,
        timeout: 600,
        authType: AuthType.UserPassword,
      },
    });
  };

  const setBigQueryInput = (bigQueryInput: IBigQueryConnectionInput) => {
    onChange({
      basic: null,
      bigQuery: {
        ...bigQueryInput,
      },
    });
  };

  const showDateDt = useDataModelerEditSeFeature();

  const { basic, bigQuery } = connection;
  const canEditProps = canEditConnectionDetails(isNewSchema, readOnly, {
    basic: basic ?? null,
    bigQuery: bigQuery ?? null,
  });

  if (!bigQuery && !basic) {
    return null;
  }
  return (
    <>
      {bigQuery ? (
        <DataModelerBigQueryConnectionInput
          readOnly={readOnly}
          canEditProps={canEditProps}
          bigQueryInput={bigQuery}
          setBigQueryInput={setBigQueryInput}
          switchToStrategy={switchFromBigQuery}
        />
      ) : (
        <DataModelerBasicConnectionInput
          readOnly={readOnly}
          canEditProps={canEditProps}
          basicInput={basic!!}
          setBasicInput={setBasicInput}
        />
      )}
      {showDateDt && (
        <VerticalField noMargin={true}>
          <DataModelerConfigLabelWithInfo
            labelText={'date_dt table name'}
            tooltipOption={dataModelLabelInfoTooltipOptions.dateDt}
          />
          <InputField
            value={dateDt}
            onChange={setDateDt}
            disabled={!canEditProps}
            placeholder={'date_dt'}
          />
        </VerticalField>
      )}
    </>
  );
};

export default DataModelerConnectionInput;
